/* eslint-disable no-nested-ternary */
import { useQuery } from "@apollo/client";
import * as Accordion from "@radix-ui/react-accordion";
import type {
  ICoursesNoPlaybackQuery,
  ICoursesNoPlaybackQueryVariables,
} from "components/black/graphql/__generated__/CoursesNoPlaybackQuery.generated";
import CoursesNoPlaybackQuery from "components/black/graphql/CoursesNoPlaybackQuery";
import { MORE_REVIEW_COMMENTS, REVIEW_COMMENTS } from "components/black/marketing/reviewComments";
import { FundamenteiBlackSignature } from "components/branding/FundamenteiBlack";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import classNames from "components/ui/classNames";
import { tailwindColors } from "components/ui/colors/colorsBundle";
import { HoverCard, HoverCardContent, HoverCardPortal, HoverCardTrigger } from "components/ui/HoverCard";
import aspectRatio from "components/utils/aspectRatio";
import { isWithinInterval, parseISO } from "date-fns";
import { atom, useAtomValue } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import useSelfie from "lib/hooks/useSelfie";
import Redirect from "lib/http/Redirect";
import formatDurationFromSeconds from "lib/utils/formatDurationFromSeconds";
import formatHoursDurationFromSeconds from "lib/utils/formatHoursDurationFromSeconds";
import getBaseUrl from "lib/utils/getBaseUrl";
import getQueryStringValue from "lib/utils/getQueryStringValue";
import stringHashCode from "lib/utils/stringHashCode";
import flatten from "lodash/flatten";
import sum from "lodash/sum";
import uniqBy from "lodash/uniqBy";
import {
  BookmarkIcon,
  ChevronDownIcon,
  CirclePlayIcon,
  ClockIcon,
  GalleryHorizontalEnd,
  GiftIcon,
  InfoIcon,
  PlusIcon,
  VideoIcon,
} from "lucide-react";
import type { NextPage } from "next";
import Link from "next/link";
import { parseAsString, useQueryState } from "nuqs";
import type { ReactElement, ReactNode } from "react";
import Countdown from "react-countdown";
import { useMount } from "react-use";

function getHotmartOfferUrl({
  isSpecialOfferWithCaixaPreta,
  isSpecialPremiumOffer,
}: {
  isSpecialOfferWithCaixaPreta?: boolean;
  isSpecialPremiumOffer?: boolean;
} = {}): string {
  if (isSpecialOfferWithCaixaPreta) {
    return "https://pay.hotmart.com/F86337115C?off=cgyrefjk";
  }
  if (isSpecialPremiumOffer) {
    return "https://pay.hotmart.com/F86337115C?off=wutan2j4";
  }
  return "https://pay.hotmart.com/F86337115C";
}

const HOTMART_OFFER_URL = atom<string | null>(null);

type BlackPageProps = {
  version?: "SPECIAL_OFFER" | "PREMIUM" | null;
};

const BlackPage: NextPage<BlackPageProps> = function BlackPage({ version }) {
  const selfie = useSelfie();
  const [selectedCourse, setSelectedCourse] = useQueryState(
    "course",
    parseAsString.withDefault("").withOptions({
      clearOnDefault: true,
    }),
  );
  const isEvergreen = true;
  const specialOfferStartsAt = parseISO("2026-08-06T03:00:00Z");
  const specialOfferEndsAt = parseISO("2026-08-17T03:00:00Z");
  const isSpecialOfferWithCaixaPreta =
    version === "SPECIAL_OFFER" ||
    isWithinInterval(new Date(), {
      start: specialOfferStartsAt,
      end: specialOfferEndsAt,
    });
  const isSpecialPremiumOffer = version === "PREMIUM";

  useHydrateAtoms([
    [
      HOTMART_OFFER_URL,
      getHotmartOfferUrl({
        isSpecialOfferWithCaixaPreta,
        isSpecialPremiumOffer,
      }),
    ],
  ]);

  useMount(() => {
    if (selectedCourse && typeof document !== "undefined") {
      document.querySelector(`[data-course-slug="${selectedCourse}"]`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  });

  const { data } = useQuery<ICoursesNoPlaybackQuery, ICoursesNoPlaybackQueryVariables>(CoursesNoPlaybackQuery, {
    skip: selfie?.isBlack,
  });

  if (selfie?.isBlack) {
    return <Redirect to="/black/courses" />;
  }

  const lives = flatten(
    data?.courses.nodes
      .filter((course) => course.name.startsWith("Lives |"))
      .map((course) => flatten(course.modules.map(({ lessons }) => lessons))),
  );
  const lessons = flatten(
    data?.courses.nodes
      .filter((course) => course.name.startsWith("Curso |"))
      .map((course) => flatten(course.modules.map(({ lessons }) => lessons))),
  );
  const livesCount = lives.length;
  const lessonsCount = lessons.length;
  const totalContentDurationInSeconds = sum([
    ...flatten(lessons.map((lesson) => lesson.media.map((media) => media.durationInSeconds))),
    ...flatten(lives.map((lesson) => lesson.media.map((media) => media.durationInSeconds))),
  ]);

  return (
    <>
      <FundamenteiMetadata
        title="Fundamentei Black"
        description="O Fundamentei Black é uma área de ensino completa com tudo o que você precisa saber para se sentir seguro ao investir na Bolsa de Valores."
      />

      <div className="dark relative isolate z-10 h-full bg-gray-900 px-6 py-24 text-center shadow-2xl sm:px-16 lg:py-16">
        <div className="pb-8">
          <FundamenteiBlackSignature
            fill="white"
            className="inline-flex"
            {...aspectRatio({
              originalWidth: 180,
              originalHeight: 65,
              desiredHeight: 38,
            })}
          />
        </div>
        <header className="mx-auto flex max-w-7xl flex-col items-center gap-y-2">
          {isSpecialOfferWithCaixaPreta ? (
            <>
              {/* Main title */}
              <h1 className="max-w-6xl text-balance text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl">
                Super Desconto no Fundamentei Black: R$500
              </h1>

              {/* Subtext */}
              <p className="max-w-2xl text-balance text-xl font-bold leading-tight text-orange-400 sm:text-2xl">
                Bônus: Caixa Preta a Custo Zero
              </p>

              {/* Urgency text with animated icon */}
              <div className="mt-4 flex items-center justify-center gap-2">
                <ClockIcon className="h-6 w-6 animate-pulse text-amber-400" strokeWidth={2} aria-hidden="true" />
                <p className="text-lg font-bold tracking-tight text-white sm:text-xl lg:text-2xl">
                  Somente até Domingo dia 16/08
                </p>
              </div>
            </>
          ) : (
            <>
              <h1 className="max-w-6xl text-balance text-4xl font-bold leading-7 tracking-tighter text-white sm:text-5xl">
                {isEvergreen
                  ? "Como atingir sua Independência Financeira ANOS antes"
                  : "A melhor área de ensino sobre Buy&Hold do Brasil."}
              </h1>
              <p className="max-w-4xl text-balance text-base font-light text-gray-300 sm:text-lg">
                {isEvergreen
                  ? `Enquanto a maioria perde tempo com tentativa e erro, você aprende o caminho certo desde o início com o Fundament Black — e acelera sua liberdade financeira com método e segurança.`
                  : `Temos tudo o que você precisa saber para se sentir seguro ao investir na Bolsa de Valores. Iniciante ou
                experiente, com o Black você irá dar o próximo passo no seu aprendizado.`}
              </p>
            </>
          )}
        </header>
        <section className="mx-auto mt-8 aspect-video w-full max-w-4xl">
          <iframe
            title="YouTube video player"
            src="https://www.youtube.com/embed/46aKwYMx3nQ?si=uUrnv37yORZ84gmo"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen={true}
            width="100%"
            height="100%"
            className="rounded-2xl"
          />
        </section>
        <BuyButton>Sim, quero ser um investidor Black!</BuyButton>

        {isSpecialOfferWithCaixaPreta && (
          <Countdown
            date={specialOfferEndsAt}
            renderer={({ days, hours, minutes, seconds, completed }) => {
              if (completed) {
                return null;
              }
              return (
                <div
                  suppressHydrationWarning={true}
                  className={classNames(
                    "mx-auto grid max-w-max grid-cols-2 justify-center gap-6 gap-x-6 rounded-4xl px-8 py-4 text-white lg:flex",
                  )}
                >
                  {days > 0 && (
                    <div className="w-28 rounded-xl bg-black/30 py-3 text-orange-50">
                      <h3 className="text-4xl font-bold sm:text-6xl">{days}</h3>
                      <strong className="font-medium">{days === 1 ? "dia" : "dias"}</strong>
                    </div>
                  )}
                  {hours > 0 && (
                    <div className="w-28 rounded-xl bg-black/30 py-3 text-orange-50">
                      <h3 className="text-4xl font-bold sm:text-6xl">{hours}</h3>
                      <strong className="font-medium">{hours === 1 ? "hora" : "horas"}</strong>
                    </div>
                  )}
                  {minutes > 0 && (
                    <div className="w-28 rounded-xl bg-black/30 py-3 text-orange-50">
                      <h3 className="text-4xl font-bold sm:text-6xl">{minutes}</h3>
                      <strong className="font-medium">{minutes === 1 ? "minuto" : "minutos"}</strong>
                    </div>
                  )}

                  <div className="w-28 rounded-xl bg-black/30 py-3 text-orange-50">
                    <h3 suppressHydrationWarning={true} className="text-4xl font-bold sm:text-6xl">
                      {seconds}
                    </h3>
                    <strong className="font-medium">{seconds === 1 ? "segundo" : "segundos"}</strong>
                  </div>
                </div>
              );
            }}
          />
        )}
        <PaymentMethods aria-hidden={true} className="mt-4 inline-flex h-5 text-center invert-50 lg:h-6" />
        <hr className="mx-auto my-16 border-white/10" />
        <Teachers />
        <hr className="mx-auto my-16 border-white/10" />
        <section id="content" className="my-16">
          <div className="pb-12">
            <div className="mx-auto max-w-7xl">
              <div className="mx-auto max-w-2xl lg:max-w-none">
                <div className="mx-auto max-w-6xl text-center">
                  <GalleryHorizontalEnd
                    className="mb-4 inline-flex size-24 text-white"
                    strokeWidth={0.8}
                    aria-hidden={true}
                  />

                  <h2 className="text-balance text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                    Nunca mais se sinta perdido na Bolsa
                  </h2>
                  <p className="mt-2 text-base text-gray-300 sm:text-lg/8">
                    Método claro passo a passo, do básico ao avançado, para você aprender no seu tempo e investir com
                    total autonomia.
                  </p>
                </div>
                {false && (
                  <dl className="mt-16 grid  grid-cols-1 gap-px overflow-hidden rounded-2xl text-center sm:grid-cols-2 lg:grid-cols-3">
                    {[
                      { name: "Aulas", value: `${lessonsCount}` },
                      { name: "Lives e Cortes", value: `${livesCount}` },
                      {
                        name: "Horas de Contéudo",
                        value: `${formatHoursDurationFromSeconds(totalContentDurationInSeconds)}+`,
                      },
                    ].map((stat) => (
                      <div key={stat.name} className="flex flex-col bg-white/5 p-8">
                        <dt className="text-sm/6 font-semibold text-gray-300">{stat.name}</dt>
                        <dd className="order-first text-5xl font-semibold tracking-tight text-white">{stat.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            </div>
          </div>

          <div
            className={classNames(
              "isolate mx-auto flex max-w-7xl overflow-hidden rounded-2xl p-2 outline outline-gray-950/5",
              "bg-slate-600/10",
              "col-span-full",
            )}
          >
            <Accordion.Root
              className="rounded-xl text-left text-white"
              type="single"
              collapsible={true}
              value={selectedCourse || ""}
              onValueChange={(course) => setSelectedCourse(course)}
            >
              {data?.courses.nodes.map((course) => {
                // All courses must have a cover
                if (!course.cover) {
                  return null;
                }

                const lessons = flatten(
                  course.modules.map(({ lessons }) =>
                    lessons.filter(({ title }) => title !== "Deixe seu comentário aqui!"),
                  ),
                );
                if (lessons.length === 0) {
                  return null;
                }
                const medias = flatten(lessons.map((lesson) => lesson.media));
                const totalCourseDuration = sum(medias.map((media) => media.durationInSeconds));

                return (
                  <Accordion.Item
                    key={course.objectId}
                    value={course.slug}
                    data-course-slug={course.slug}
                    className="py-1 first:pt-0 last:pb-0"
                  >
                    <Accordion.Trigger
                      className={classNames(
                        "flex w-full flex-1 items-center justify-between gap-x-8 rounded-xl",
                        "data-[state=open]:bg-orange-400/10 hover:bg-orange-400/5",
                        "group",
                        "flex-col sm:flex-row",
                      )}
                    >
                      <div className="flex w-full flex-col gap-x-4 sm:flex-row">
                        <div className="flex aspect-video w-full py-2 pl-1.5 hover:opacity-80 md:h-[165px] md:w-max md:max-w-[294px]">
                          <img className="w-full rounded-xl" src={course.cover.url} alt={course.name} />
                        </div>
                        <div className="flex flex-col px-4 py-2 text-left sm:px-0 sm:py-4">
                          <h4
                            className={classNames(
                              "text-xl font-black tracking-tight text-neutral-50 sm:text-2xl",
                              "group-hover:text-orange-100",
                              "group-data-[state=open]:text-orange-400",
                            )}
                          >
                            {course.name.replace("Curso |", "").trim()}
                          </h4>
                          <p className="pt-2 font-light tracking-normal text-neutral-300 sm:pt-0 sm:text-base sm:leading-relaxed">
                            {course.description}
                          </p>
                          <div className="mt-4 flex items-center gap-x-2 sm:mt-auto">
                            {lessons.length > 0 && (
                              <div className="inline-flex items-center gap-x-1.5 rounded-lg bg-orange-200/10 p-1 px-2.5 text-sm font-bold text-orange-300">
                                <VideoIcon className="h-3 w-3" strokeWidth={3} />
                                {course.name.includes("Lives |") ? (
                                  <span>
                                    {lessons.length} {lessons.length > 1 ? "lives" : "live"}
                                  </span>
                                ) : (
                                  <span>
                                    {lessons.length} {lessons.length > 1 ? "aulas" : "aula"}
                                  </span>
                                )}
                              </div>
                            )}

                            {totalCourseDuration > 0 && (
                              <div className="inline-flex items-center gap-x-1.5 rounded-lg bg-neutral-200/10 p-1 px-2.5 text-sm font-bold text-neutral-300">
                                <ClockIcon className="h-4 w-4" />
                                <span>{formatDurationFromSeconds(totalCourseDuration)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex self-stretch p-1.5">
                        <div className="ml-auto flex flex-1 flex-col items-center justify-center rounded-lg bg-neutral-100/10 p-4">
                          <ChevronDownIcon
                            className={classNames(
                              "h-6 w-6 transition-transform duration-300",
                              "group-data-[state=open]:rotate-180",
                            )}
                          />
                        </div>
                      </div>
                    </Accordion.Trigger>
                    <Accordion.Content className="p-2">
                      <div>
                        <ul className="flex flex-col gap-y-2">
                          {uniqBy(
                            lessons.filter(({ title }) => title !== "Deixe seu comentário aqui!"),
                            "objectId",
                          ).map((lesson) => {
                            const runtime = sum(lesson.media.map((media) => media.durationInSeconds));
                            const chapters = flatten(lesson.media.map((media) => media.chapters));
                            return (
                              <li
                                key={lesson.objectId}
                                className="flex flex-col gap-y-2 rounded-xl p-2 sm:flex-row sm:justify-between sm:gap-y-0"
                              >
                                <div className="flex items-center gap-x-2 sm:gap-x-4">
                                  <div>
                                    <CirclePlayIcon
                                      className="size-5 fill-orange-300/10 stroke-orange-400 sm:size-6"
                                      strokeWidth={2}
                                    />
                                  </div>
                                  <span className="text-sm font-medium tracking-tight sm:text-base">
                                    {lesson.title}
                                  </span>
                                </div>
                                <div className="flex items-center gap-x-2 empty:hidden sm:gap-0">
                                  {chapters.length > 0 && (
                                    <HoverCard openDelay={200} closeDelay={100}>
                                      <HoverCardTrigger asChild={true}>
                                        <div
                                          className={classNames(
                                            "order-2 inline-flex items-center gap-x-1.5 rounded-lg bg-lime-200/10 font-bold text-lime-300",
                                            "p-1 px-2 text-xs",
                                            "sm:p-1 sm:px-2.5 sm:text-sm",
                                            "cursor-pointer",
                                          )}
                                        >
                                          <BookmarkIcon className="h-3 w-3" strokeWidth={3} />
                                          <span>
                                            {chapters.length} {chapters.length > 1 ? "capítulos" : "capítulo"}
                                          </span>
                                        </div>
                                      </HoverCardTrigger>
                                      <HoverCardPortal>
                                        <HoverCardContent
                                          side="top"
                                          sideOffset={12}
                                          className="w-80 border-neutral-500/40 bg-neutral-800 p-1 shadow-2xl shadow-slate-800 sm:w-96"
                                        >
                                          <ul className="flex flex-col gap-y-1">
                                            {chapters.map((chapter) => (
                                              <li
                                                key={chapter.title}
                                                className="flex items-center justify-between rounded-lg px-2 py-1"
                                              >
                                                <span className="truncate text-sm font-medium tracking-wide text-slate-100">
                                                  {chapter.title.replace(/^-/, "").trim()}
                                                </span>
                                                <div className="inline-flex items-center gap-x-1.5 rounded-lg bg-transparent p-1 px-2.5 text-sm font-bold text-neutral-300">
                                                  <ClockIcon className="h-4 w-4" />
                                                  <span className="font-mono text-xs">
                                                    {chapter.formattedStartTime}
                                                  </span>
                                                </div>
                                              </li>
                                            ))}
                                          </ul>
                                        </HoverCardContent>
                                      </HoverCardPortal>
                                    </HoverCard>
                                  )}
                                  {runtime > 0 && (
                                    <div className="order-1 inline-flex items-center gap-x-1.5 rounded-lg bg-transparent text-sm font-bold text-neutral-300 sm:order-2 sm:p-1 sm:px-2.5">
                                      <ClockIcon className="size-5" />
                                      <span className="font-mono">{formatDurationFromSeconds(runtime)}</span>
                                    </div>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </Accordion.Content>
                  </Accordion.Item>
                );
              })}
            </Accordion.Root>
          </div>
        </section>
        <LegacyContentPricing isSpecialOfferWithCaixaPreta={isSpecialOfferWithCaixaPreta} />
        <PremiumIncluded />
        <RenewWithDiscount />
        {isSpecialOfferWithCaixaPreta && <CaixaPretaBonus />}
        <FinalPrice
          isSpecialOfferWithCaixaPreta={isSpecialOfferWithCaixaPreta}
          isSpecialPremiumOffer={isSpecialPremiumOffer}
        />
        <Testimonials
          courses={data?.courses.nodes || []}
          topShelfTestimonials={REVIEW_COMMENTS}
          bottomShelfTestimonials={MORE_REVIEW_COMMENTS}
        />
        <Footer />

        <svg
          viewBox="0 0 1024 1024"
          aria-hidden={true}
          className="absolute left-1/2 top-0 -z-10 -translate-x-1/2 [mask-image:radial-gradient(farthest-side,white,transparent)]"
        >
          <circle r={512} cx={512} cy={512} fill="url(#827591b1-ce8c-4110-b064-7cb85a0b1217)" fillOpacity="0.3" />
          <defs>
            {isSpecialOfferWithCaixaPreta ? (
              <radialGradient id="827591b1-ce8c-4110-b064-7cb85a0b1217">
                <stop stopColor={tailwindColors.sky[200]} />
                <stop offset={0} stopColor={tailwindColors.sky[400]} />
              </radialGradient>
            ) : (
              <radialGradient id="827591b1-ce8c-4110-b064-7cb85a0b1217">
                <stop stopColor={tailwindColors.teal[300]} />
                <stop offset={0} stopColor={tailwindColors.teal[300]} />
              </radialGradient>
            )}
          </defs>
        </svg>
      </div>
    </>
  );
};

interface BuyButtonProps {
  href?: string;
  children: ReactNode;
  className?: string;
  variant?: "teal" | "lime" | "indigo";
}
function BuyButton({ href, children, className, variant = "teal", ...props }: BuyButtonProps): ReactElement {
  const hotmartOfferUrl = useAtomValue(HOTMART_OFFER_URL);
  return (
    <a
      href={href || hotmartOfferUrl || getHotmartOfferUrl()}
      target="_self"
      rel="noopener noreferrer"
      className={classNames(
        "mx-auto my-12 flex w-full max-w-2xl items-center justify-center rounded-[32px] p-4 text-center text-base font-medium shadow-sm md:p-6 md:text-3xl",
        "tracking-tight hover:rounded-2xl hover:tracking-tighter",
        "border transition-all",
        className,
        {
          "border-teal-950 bg-teal-900 text-teal-100 hover:bg-teal-800 hover:text-teal-200": variant === "teal",
          "rounded-none border-lime-950 bg-lime-300 text-lime-950 hover:rounded-none hover:bg-lime-800 hover:text-lime-200":
            variant === "lime",
          "border-indigo-950 bg-indigo-900 text-indigo-50 hover:bg-indigo-800 hover:text-indigo-200":
            variant === "indigo",
        },
      )}
      {...props}
    >
      {children}
    </a>
  );
}

function Teachers() {
  const people = [
    {
      fullName: "Eduardo Cavalcanti",
      pictureUrl: `${getBaseUrl()}/static/people/v2/eduardo.png`,
      jobTitle: "CEO, Cofundador",
      biography:
        "Pós-graduado em Finanças, Investimentos e Banking pela PUCRS, Analisa CNPI pela APIMEC e investidor há 13 anos na Bolsa de Valores, com mais de 5.000 alunos tendo passado pelos seus cursos 100% focados no método Buy&Hold.",
    },
    {
      fullName: "Arthur Svidzinski",
      pictureUrl: `${getBaseUrl()}/static/people/v2/asvid.png`,
      jobTitle: "Analista CNPI",
      biography:
        "Analista CNPI pela APIMEC e investidor há mais de 15 anos. Desde 2019 atua de forma ativa no seu canal do YouTube ajudando pessoas que desejam aprender mais sobre análise de fundamentalista de Ações, Stocks e REITs, visando escolher os melhores ativos para uma carteira de longo prazo focada no método Buy&Hold.",
    },
  ];
  return (
    <section>
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <header className="mx-auto sm:text-center">
          <h2 className="text-balance text-4xl font-bold tracking-tighter text-white sm:text-4xl">
            Conheça os professores
          </h2>
          <p className="mt-2 text-balance text-base text-gray-300 sm:text-lg/8">
            No comando do Black proporcionando todo o suporte necessário para você.
          </p>
        </header>
        <ul className="mx-auto mt-12 flex flex-col gap-y-20 sm:mt-24">
          {people.map((person) => (
            <li key={person.fullName} className="flex flex-col items-center gap-8 sm:items-start xl:flex-row">
              <img
                alt={person.fullName}
                src={person.pictureUrl}
                className="w-32 flex-none rounded-full object-cover ring-8 ring-white/5 sm:aspect-[4/5] sm:w-48 sm:rounded-3xl"
              />

              <div className="flex-auto text-center sm:text-left">
                <strong className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">
                  {person.fullName}
                </strong>
                <p className="text-base/8 font-medium text-blue-300">{person.jobTitle}</p>
                <p className="mt-4 text-base/7 tracking-wide text-gray-300">{person.biography}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function FundamenteiBlackRubrikIcon({ className, strokeWidth = 20, ...props }: React.SVGProps<any>): ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 280 280"
      className={classNames("inline-flex fill-black", className)}
      {...props}
    >
      <title>Fundamentei Black</title>
      <path
        fillRule="nonzero"
        strokeWidth={strokeWidth}
        d="M229.17 137.286c-25.704 4.315-48.15 9.488-67.357 14.75-.963-.528-2.144-.925-3.107-1.32a121.599 121.599 0 0 0-18.442-4.58l5.273-.792a241.974 241.974 0 0 0 38.065-9.159c10.785-3.544 20.826-7.991 29.686-12.967 8.139-4.579 15.073-9.686 20.586-15.058 7.176-6.935 11.486-14.024 12.689-20.694 1.444-6.803-.722-12.967-5.994-17.81-4.07-3.545-9.823-6.297-16.999-8.124-7.175-1.827-16.035-2.752-26.098-2.488a274.956 274.956 0 0 0-60.577 7.992c-17.96 4.447-35.68 10.611-51.235 17.942-30.167 14.266-50.272 31.57-52.438 45.461-.962 5.636 1.204 10.612 6.454 14.53 5.513 4.183 14.132 6.935 25.617 8.256 2.626.396 5.513-.528 5.995-2.092.721-1.43-.963-3.016-3.829-3.28-17.48-2.091-25.377-7.86-23.933-17.017.963-5.9 5.994-12.835 14.373-20.166 8.62-7.463 20.586-14.926 34.478-21.487 14.854-6.935 31.611-12.835 48.85-17.018 18.923-4.579 37.825-7.199 56.507-7.463 16.036-.396 27.783 2.356 33.996 7.86 8.62 7.86 5.995 20.298-6.694 32.868-5.031 4.976-11.485 9.555-19.164 14.024-8.138 4.579-17.48 8.652-27.783 12.042-11.004 3.677-22.992 6.539-35.44 8.388a264.083 264.083 0 0 1-24.896 2.752c7.898-8.652 17.48-17.81 28.484-26.33 1.684-1.321.962-3.016-1.203-3.941-2.385-.925-5.513-.528-7.176.66-12.688 9.687-23.474 20.43-32.312 30.117h-5.994c-2.866 0-5.032 1.189-5.032 2.752 0 1.563 2.144 2.884 5.032 2.884h.963c-9.342 10.875-15.314 19.637-16.998 22.653-.963 1.431-3.107 4.976 1.203 6.67.722.265 1.444.397 2.144.53-11.726 5.635-21.549 11.271-28.484 16.51-4.79 3.545-8.138 6.803-10.304 9.82-2.384 3.28-2.865 6.164-1.925 8.783.963 2.62 4.31 6.032 14.373 7.596 3.107.528 7.176.66 11.485.66 5.032 0 10.786-.264 17.24-.925 26.58-2.884 50.753-9.95 67.992-19.901 16.757-9.687 25.136-20.959 23.473-31.042-.962-5.371-4.55-10.082-10.785-14.023 11.485-3.61 40.144-9.114 54.867-11.888 5.206-.969 21.81-10.545 4.375-6.935Zm-117.626 11.736c16.156.665 29.467 2.638 39.456 5.94-23.061 6.85-40.172 13.455-51.572 18.198-1.434.665-2.845 1.308-4.278 1.84-.24-.399-.478-.665-.956-1.064-.239-.266-.716-.399-1.194-.665 1.91-3.037 8.556-12.656 18.544-24.271v.022Zm58.275 21.757c.718 3.999-.718 8.54-4.047 13.06-3.57 4.521-9.292 9.041-16.43 13.192-15.711 9.04-37.864 15.517-62.15 18.103-25.721 2.586-30.248-1.282-30.966-2.977-1.197-3.108 2.372-8.15 10.25-13.974 8.813-6.454 22.392-13.713 39.061-20.689 15.95-6.585 34.535-12.93 54.294-18.494 1.915 1.174 3.569 2.325 5.005 3.63 2.85 2.455 4.526 5.172 5.005 8.149h-.022Z"
      />
    </svg>
  );
}

interface LegacyContentPricingProps {
  isSpecialOfferWithCaixaPreta?: boolean;
}
function LegacyContentPricing({ isSpecialOfferWithCaixaPreta }: LegacyContentPricingProps) {
  return (
    <section className="my-24 rounded-3xl">
      <div className="mx-auto flex max-w-7xl flex-col gap-y-8 px-8 text-left">
        <figure className="relative isolate pt-6 sm:pt-12">
          {/* Quote */}
          <svg
            fill="none"
            viewBox="0 0 162 128"
            aria-hidden="true"
            strokeWidth={1.5}
            className="absolute left-0 top-0 -z-10 h-24 fill-sky-600/20"
          >
            <path
              d="M65.5697 118.507L65.8918 118.89C68.9503 116.314 71.367 113.253 73.1386 109.71C74.9162 106.155 75.8027 102.28 75.8027 98.0919C75.8027 94.237 75.16 90.6155 73.8708 87.2314C72.5851 83.8565 70.8137 80.9533 68.553 78.5292C66.4529 76.1079 63.9476 74.2482 61.0407 72.9536C58.2795 71.4949 55.276 70.767 52.0386 70.767C48.9935 70.767 46.4686 71.1668 44.4872 71.9924L44.4799 71.9955L44.4726 71.9988C42.7101 72.7999 41.1035 73.6831 39.6544 74.6492C38.2407 75.5916 36.8279 76.455 35.4159 77.2394L35.4047 77.2457L35.3938 77.2525C34.2318 77.9787 32.6713 78.3634 30.6736 78.3634C29.0405 78.3634 27.5131 77.2868 26.1274 74.8257C24.7483 72.2185 24.0519 69.2166 24.0519 65.8071C24.0519 60.0311 25.3782 54.4081 28.0373 48.9335C30.703 43.4454 34.3114 38.345 38.8667 33.6325C43.5812 28.761 49.0045 24.5159 55.1389 20.8979C60.1667 18.0071 65.4966 15.6179 71.1291 13.7305C73.8626 12.8145 75.8027 10.2968 75.8027 7.38572C75.8027 3.6497 72.6341 0.62247 68.8814 1.1527C61.1635 2.2432 53.7398 4.41426 46.6119 7.66522C37.5369 11.6459 29.5729 17.0612 22.7236 23.9105C16.0322 30.6019 10.618 38.4859 6.47981 47.558L6.47976 47.558L6.47682 47.5647C2.4901 56.6544 0.5 66.6148 0.5 77.4391C0.5 84.2996 1.61702 90.7679 3.85425 96.8404L3.8558 96.8445C6.08991 102.749 9.12394 108.02 12.959 112.654L12.959 112.654L12.9646 112.661C16.8027 117.138 21.2829 120.739 26.4034 123.459L26.4033 123.459L26.4144 123.465C31.5505 126.033 37.0873 127.316 43.0178 127.316C47.5035 127.316 51.6783 126.595 55.5376 125.148L55.5376 125.148L55.5477 125.144C59.5516 123.542 63.0052 121.456 65.9019 118.881L65.5697 118.507Z"
              id="b56e9dab-6ccb-4d32-ad02-6b4bb5d9bbeb"
            />
            <use x={86} href="#b56e9dab-6ccb-4d32-ad02-6b4bb5d9bbeb" />
          </svg>

          <blockquote className="flex flex-col gap-y-6 text-base font-light tracking-wide text-slate-100 sm:text-xl">
            <p>
              Em 2021 eu parei de vender os meus cursos de Ações, FIIs e Stocks. Para você ter acesso apenas a esse
              conteúdo o valor total era de R$5.988! Se corrigir o preço apenas pela inflação daquela época até hoje,
              ele seria bem maior.
            </p>

            <p>
              No Fundamentei Black você terá acesso à diversos conteúdos, além dos cursos que vamos fazer ao longo dos
              anos, ou seja, o valor que você vai receber é extremamente maior do que esse total.
            </p>

            <p>O custo benefício do Black é tão absurdo que você não tem como ficar de fora!</p>

            <p className="font-medium">—Eduardo Cavalcanti</p>
          </blockquote>
        </figure>

        <strong className="text-4xl font-medium tracking-tighter text-white sm:text-5xl">
          Valor apenas dos meus cursos
        </strong>

        <ul
          className={classNames(
            "flex flex-col gap-y-3 text-pretty text-lg text-slate-300",
            "[&>li>span]:text-xl [&>li>span]:text-white",
            "[&>li>strong]:text-blue-300",
          )}
        >
          <li className="flex items-center gap-x-2">
            <strong className="text-2xl font-medium sm:text-4xl">R$997</strong> → <span>Investidor Águia</span>
          </li>
          <li className="flex items-center gap-x-2">
            <strong className="text-2xl font-medium sm:text-4xl">R$997</strong> → <span>Investidor Águia Avançado</span>
          </li>
          <li className="flex items-center gap-x-2">
            <strong className="text-2xl font-medium sm:text-4xl">R$1.997</strong> → <span>Investidor Águia FIIs</span>
          </li>
          <li className="flex items-center gap-x-2">
            <strong className="text-2xl font-medium sm:text-4xl">R$1.997</strong> → <span>Investidor Águia Stocks</span>
          </li>

          {isSpecialOfferWithCaixaPreta && (
            <li className="flex items-center gap-x-2">
              <strong className="text-2xl font-medium sm:text-4xl">R$997</strong> → <span>Caixa Preta</span>
            </li>
          )}

          {isSpecialOfferWithCaixaPreta ? (
            <li className="mt-4 border-t border-blue-400/10 pt-4">
              <strong className="text-4xl font-bold">R$6.985</strong>
            </li>
          ) : (
            <li className="mt-4 border-t border-blue-400/10 pt-4">
              <strong className="text-4xl font-bold">R$5.988</strong>
            </li>
          )}
        </ul>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="text-left">
      <div className="mx-auto max-w-7xl gap-y-2 space-y-4 px-6">
        <p className="text-sm/6 font-medium text-white">
          &copy; {new Date().getFullYear()} Fundamentei • CNPJ: 35.804.692/0001-05
        </p>
        <p className="text-sm tracking-wider text-slate-400">
          Aviso legal: Todas as estratégias e investimentos envolvem risco de perda. Nenhuma informação contida neste
          produto deve ser interpretada como uma garantia de resultados ou aconselhamento de investimento.
        </p>
        <p className="text-sm/6 tracking-wide text-slate-300">
          {"Suporte e dúvidas: "}
          <a className="font-medium text-blue-200 hover:underline" href="mailto:contato@fundamentei.com">
            contato@fundamentei.com
          </a>
        </p>
        <hr className="my-4 border-slate-800" />
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <a
            className="text-sm font-medium tracking-wide text-slate-200 hover:underline"
            rel="noopener noreferrer"
            target="_blank"
            href={`${getBaseUrl()}/privacy`}
          >
            Política de Privacidade
          </a>
          <span className="hidden text-slate-300 sm:inline-flex">•</span>
          <a
            className="text-sm font-medium tracking-wide text-slate-200 hover:underline"
            rel="noopener noreferrer"
            target="_blank"
            href={`${getBaseUrl()}/terms`}
          >
            Termos de Uso
          </a>
          <span className="hidden text-slate-300 sm:inline-flex">•</span>
          <a
            className="text-sm font-medium tracking-wide text-slate-200 hover:underline"
            rel="noopener noreferrer"
            target="_blank"
            href={`${getBaseUrl()}/anti-piracy`}
          >
            Política Anti-Pirataria
          </a>
        </div>
      </div>
    </footer>
  );
}

interface FinalPriceProps {
  isSpecialOfferWithCaixaPreta: boolean;
  isSpecialPremiumOffer: boolean;
}

function FinalPrice({ isSpecialOfferWithCaixaPreta, isSpecialPremiumOffer }: FinalPriceProps) {
  return (
    <section className="my-24 rounded-3xl bg-indigo-900/30 py-8 2xl:py-20">
      <div className="mx-auto flex max-w-7xl flex-col gap-y-8 px-8 text-left 2xl:px-8">
        <FundamenteiBlackRubrikIcon className="w-16 fill-white sm:w-32" aria-hidden={true} />

        <ul
          className={classNames(
            "flex flex-col gap-y-3 text-pretty text-lg text-slate-300",
            "[&>li>span]:text-lg [&>li>span]:text-white [&>li>span]:sm:text-xl",
            "[&>li>strong]:font-medium [&>li>strong]:text-rose-300",
          )}
        >
          <li className="flex flex-col gap-x-2 sm:flex-row sm:items-center">
            <strong className="text-4xl">R$5.988</strong> <span>→ Curso de Ações, FIIs e Stocks</span>
          </li>
          <li className="flex flex-col gap-x-2 sm:flex-row sm:items-center">
            <strong className="text-4xl">R$1.000</strong> <span>→ Desconto Renovação Anual</span>
          </li>

          {isSpecialOfferWithCaixaPreta && (
            <li className="flex flex-col gap-x-2 sm:flex-row sm:items-center">
              <strong className="text-4xl">R$997</strong> <span>→ Caixa Preta</span>
            </li>
          )}
          <li className="flex flex-col gap-x-2 sm:flex-row sm:items-center">
            <strong className="text-4xl">R$320</strong> <span>→ Acesso Premium Anual</span>
          </li>

          {isSpecialOfferWithCaixaPreta ? (
            <li>
              {/* 5988 + 1000 + 997 + 320 = 8305 */}
              <strong className="text-4xl line-through">R$8.305</strong>
            </li>
          ) : (
            <li>
              <strong className="text-4xl line-through">R$7.308</strong>
            </li>
          )}
        </ul>

        <hr className="border-yellow-400/10" />

        <p className="text-balance text-2xl tracking-tighter text-white sm:text-4xl">
          E hoje você poderá se inscrever por apenas:
        </p>

        {isSpecialOfferWithCaixaPreta && (
          <div className="flex flex-col gap-y-2">
            <strong className="text-pretty text-4xl font-bold text-white sm:text-6xl">12x de R$154,82</strong>
            <span className="text-2xl text-slate-300 sm:text-3xl">ou R$1.497 à vista</span>
          </div>
        )}

        {isSpecialPremiumOffer && (
          <div className="flex flex-col gap-y-2">
            <strong className="text-pretty text-4xl font-bold text-white sm:text-6xl">12x de R$134,14</strong>
            <span className="text-2xl text-slate-300 sm:text-3xl">ou R$1.297 à vista</span>
          </div>
        )}

        {!isSpecialOfferWithCaixaPreta && !isSpecialPremiumOffer && (
          <div className="flex flex-col gap-y-2">
            <strong className="text-pretty text-4xl font-bold text-white sm:text-6xl">12x de R$206,54</strong>
            <span className="text-2xl text-slate-300 sm:text-3xl">ou R$1.997 à vista</span>
          </div>
        )}

        <BuyButton className="mx-0 my-4 sm:my-6" variant="indigo">
          Quero a minha vaga!
        </BuyButton>
      </div>
    </section>
  );
}

function RenewWithDiscount() {
  return (
    <section className="my-12">
      <div className="mx-auto flex max-w-7xl flex-col gap-y-8 px-4 text-left sm:px-8">
        <GiftIcon className="size-16 text-white" strokeWidth={1} aria-hidden={true} />

        <h2 className="text-3xl font-bold tracking-tighter text-white sm:text-5xl">
          Renove com <span className="text-lime-300">50% de desconto.</span>
        </h2>

        <p className="text-pretty text-base tracking-wide text-gray-200 sm:text-lg">
          Vamos sempre evoluir e adicionar mais conteúdo dentro do Fundamentei Black, além de atualizar o que for
          ficando antigo, então caso você queira permanecer por mais 1 ano na comunidade, poderá fazer isso com 50% de
          desconto em relação ao preço padrão que hoje é de R$1.997, ou seja, por menos de R$1.000 você renova o Black e
          o Premium por mais 1 ano!
        </p>
        <p className="text-pretty text-base tracking-wide text-gray-200 sm:text-lg">
          Considerando os valores atuais e que você renovaria pelo menos o Premium anual no Fundamentei, o Black sairia
          por volta de R$500 nessa renovação, o que é um valor excelente para o nível de conteúdo que você tem acesso.
        </p>
        <p className="text-pretty text-base tracking-wide text-gray-200 sm:text-lg">
          Se o Fundamentei Black aumentar no futuro para R$3 mil, por exemplo, você poderá renovar sempre com 50% de
          desconto, no caso R$1.500 já contemplando o Premium, e por aí vai—fazemos questão de sempre manter esse bônus
          para quem confia no nosso trabalho.
        </p>
      </div>
    </section>
  );
}

function CaixaPretaBonus() {
  return (
    <section className="my-12">
      <div
        className={classNames(
          "mx-auto flex max-w-7xl flex-col gap-y-8 p-8 text-left",
          "sm:p-16",
          "lg:p-24 lg:pt-16",
          "rounded-3xl border-orange-300 bg-orange-700/10",
        )}
      >
        <PlusIcon className="size-12 text-white lg:size-16" strokeWidth={1} aria-hidden={true} />

        <h2 className="text-3xl font-bold tracking-tighter text-white sm:text-5xl">
          Bônus Especial: <span className="text-orange-400">Caixa Preta</span>
        </h2>

        <p className="text-pretty text-base tracking-wide text-gray-200 sm:text-lg">
          Nesse curso eu irei revelar todas as Ações, Fundos Imobiliários, Stocks e REITs da minha carteira e quais
          foram os critérios que utilizei para escolher cada um desses ativos.
        </p>

        <p className="text-pretty text-base tracking-wide text-gray-200 sm:text-lg">
          O que estou te oferecendo aqui é a minha experiência e o meu conhecimento acumulado em mais de 10 anos na
          Bolsa de Valores para que você entenda como a mente de um investidor experiente funciona e possa tomar as suas
          próprias decisões de forma mais assertiva.
        </p>

        <p className="text-pretty text-base tracking-wide text-gray-200 sm:text-lg">
          A primeira turma em 2021 foi em formato de Workshop ao vivo e as posteriores tinham acesso ao conteúdo
          gravado, mas recentemente eu regravei todas as aulas do zero com um conteúdo mais atualizado e objetivo! Você
          terá acesso vitalício ao curso e as atualizações futuras de forma gratuita e ilimitada, além de poder contar
          com o meu suporte pessoal e diferenciado dentro do curso (ele é feito exclusivamente por mim).
        </p>

        <p className="text-pretty text-base font-bold tracking-wide text-orange-300 sm:text-lg">
          P.S: Lembrando que o curso não tem como objetivo recomendar a compra ou venda de qualquer ativo.
        </p>
      </div>
    </section>
  );
}

interface TestimonialsProps {
  courses: ICoursesNoPlaybackQuery["courses"]["nodes"];
  topShelfTestimonials: {
    text: string;
    fullName: string;
    course?: string;
  }[];
  bottomShelfTestimonials: {
    text: string;
    fullName: string;
    course?: string;
  }[];
}
function Testimonials({ courses, topShelfTestimonials, bottomShelfTestimonials }: TestimonialsProps) {
  return (
    <div>
      <div className="mx-auto max-w-7xl text-left">
        <div className="px-6 text-left">
          <h2 className="text-base/7 font-semibold uppercase text-cyan-400">Depoimentos</h2>

          <p className="mt-2 text-pretty text-3xl font-semibold tracking-tight text-white sm:text-5xl">
            Veja alguns comentários que recebemos no Black
          </p>
        </div>

        <div className="mx-auto mt-12 flow-root max-w-2xl sm:mt-20 lg:mx-0 lg:max-w-none">
          <div className="-mt-8 sm:-mx-4 sm:columns-2 sm:text-[0] lg:columns-2">
            {topShelfTestimonials.map((testimonial) => (
              <div key={stringHashCode(testimonial.text)} className="pt-8 sm:inline-block sm:w-full sm:px-4">
                <div className="rounded-3xl bg-gray-800 p-6 text-sm/6 sm:p-8">
                  <blockquote className="text-left text-sm/6 tracking-wider text-zinc-200 sm:text-base/7">
                    <p>{`“${testimonial.text}”`}</p>
                  </blockquote>
                  <div className="mt-2 text-left font-bold text-cyan-100 sm:mt-6 sm:text-base">
                    — {testimonial.fullName}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <BuyButton>Quero participar do Black!</BuyButton>

        {/* Bottom */}
        <div className="text-center">
          <h2 className="mt-12 text-pretty text-2xl font-bold tracking-tight text-white sm:text-4xl">
            Não acredito! Chegou até aqui e ainda não se convenceu?
          </h2>
          <p className="mt-2 text-base tracking-wide text-gray-300 sm:text-lg">
            Confira mais alguns reviews que vão te convencer sobre o poder do Black!
          </p>
        </div>

        <div className="mx-auto mt-12 flow-root max-w-2xl lg:mx-0 lg:max-w-none">
          <div className="-mt-8 sm:-mx-4 sm:columns-2 sm:text-[0] lg:columns-2">
            {bottomShelfTestimonials.map((testimonial) => {
              const course = courses.find(({ name }) => {
                return testimonial.course && name.includes(testimonial.course);
              });
              return (
                <div key={stringHashCode(testimonial.text)} className="pt-8 sm:inline-block sm:w-full sm:px-4">
                  <div className="rounded-3xl bg-gray-800 p-6 text-sm/6 sm:p-8">
                    <blockquote className="text-left text-sm/6 tracking-wider text-zinc-200 sm:text-base/7">
                      <p>{`“${testimonial.text}”`}</p>
                    </blockquote>
                    <div className="mt-2 text-left font-bold text-cyan-100 sm:mt-6 sm:text-base">
                      — {testimonial.fullName}
                    </div>
                    {course && (
                      <Link
                        href={`/black/?course=${course.slug}`}
                        scroll={false}
                        className="mt-2 inline-flex text-sm font-bold tracking-wide text-orange-200 hover:underline sm:text-base"
                        onClick={() => {
                          document.querySelector(`[data-course-slug="${course.slug}"]`)?.scrollIntoView({
                            behavior: "instant",
                            block: "start",
                          });
                        }}
                      >
                        {testimonial.course}
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <BuyButton>Agora sim. Quero participar do Black!</BuyButton>
        </div>
      </div>
    </div>
  );
}

function PremiumIncluded() {
  const features = [
    {
      name: "Tabela de balanços completa",
      description:
        "É o que todo investidor precisa para fazer uma análise fundamentalista completa. Sem isso não dá pra saber nada.",
    },
    {
      name: "Gráficos. Muitos!",
      description: "E todos eles só vão facilitar sua vida durante sua análise/estudos com foco no longo prazo.",
    },
    {
      name: "Ranking",
      description:
        "Com base nas notas dos usuários, fazemos uma média geral e montamos uma lista com as empresas mais bem avaliadas.",
    },
  ];

  return (
    <section className="overflow-hidden rounded-3xl bg-slate-500/10 py-8 text-left sm:py-12 2xl:py-32">
      <div className="mx-auto max-w-7xl lg:px-8">
        <div className="grid grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:grid-cols-2 lg:items-start">
          <div className="px-6 md:px-0 lg:pr-4 lg:pt-4">
            <div className="mx-auto lg:mx-0">
              <h2 className="text-xs font-semibold uppercase text-blue-200 sm:text-base/7">
                Todas as ferramentas necessárias
              </h2>

              <p className="mt-2 text-pretty text-3xl font-semibold tracking-tight text-white sm:text-5xl">
                Comprando o Black, você também leva o Premium.
              </p>
              <p className="mt-6 text-base/7 text-gray-300">
                É isso mesmo! Sendo Black você terá acesso completo ao site, afinal, faz sentido poder acessar todos os
                recursos do site para te ajudar com os seus estudos durante os cursos.
              </p>
              <p className="mt-6 text-base/7 text-gray-300">
                Isso contempla um valor a mais de até R$320 equivalente ao plano Premium com acesso por 12 meses e sai
                de graça para você hoje! Para você que é assinante do plano mensal, a economia é maior ainda de R$588!
              </p>
              <dl className="mt-6 max-w-xl space-y-8 text-sm/8 text-gray-600 sm:text-base/7 lg:max-w-none">
                {features.map((feature) => (
                  <div key={feature.name} className="space-x-1.5">
                    <dt className="inline rounded-xl bg-sky-400/10 px-2 py-1.5 font-semibold text-sky-200">
                      {feature.name}
                    </dt>
                    <dd className="inline text-base tracking-wide text-sky-100">{feature.description}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6 flex flex-col items-center justify-center gap-x-4 gap-y-6 rounded-xl bg-sky-600/5 p-6 sm:flex-row sm:items-start sm:justify-normal">
                <div aria-hidden={true}>
                  <InfoIcon className="size-6 text-sky-200" />
                </div>
                <p className="-mt-1 text-balance text-base font-medium text-white sm:text-sm sm:tracking-wider">
                  Não se preocupe caso você seja nosso assinante Premium Anual e possua um valor remanescente acima de
                  R$50, faremos o reembolso proporcional em aproximadamente 7 dias da data da sua inscrição no Black.{" "}
                  <span className="font-normal text-gray-200">Aqui sempre jogamos limpo com os nossos clientes!</span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-y-12 px-6 lg:px-0">
            <div className="relative isolate overflow-hidden rounded-3xl bg-blue-500 pl-6 pt-8 sm:mx-auto sm:max-w-2xl sm:pl-16 sm:pr-0 sm:pt-16 lg:mx-0 lg:max-w-none">
              <div
                className="absolute -inset-y-px -left-3 -z-10 w-full origin-bottom-left skew-x-[-20deg] bg-blue-200 shadow-xl"
                aria-hidden="true"
              />
              <div className="mx-auto max-w-2xl sm:mx-0 sm:max-w-none">
                <div className="h-80 bg-[url(/static/landing/screenshots/pt/black/tinified/wege3-financials-dark-mode.png)] bg-[size:1280px_508px] bg-[left_-128px_top_0px] bg-no-repeat" />
              </div>
              <div
                className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/10 sm:rounded-3xl"
                aria-hidden="true"
              />
            </div>

            <div className="relative isolate overflow-hidden rounded-3xl bg-sky-500 pl-6 pt-8 sm:mx-auto sm:max-w-2xl sm:pl-16 sm:pr-0 sm:pt-16 lg:mx-0 lg:max-w-none">
              <div
                className="absolute -inset-y-px -left-3 -z-10 w-full origin-bottom-left skew-x-[-20deg] bg-blue-200 shadow-xl"
                aria-hidden="true"
              />
              <div className="mx-auto max-w-2xl sm:mx-0 sm:max-w-none">
                <div className="h-80 rounded-tl-2xl bg-[url(/static/landing/screenshots/pt/black/tinified/wege3-financials-comparison-shul.png)] bg-[size:930px_464px] bg-[left_0px_top_0px] bg-no-repeat shadow" />
              </div>
              <div
                className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/10 sm:rounded-3xl"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PaymentMethods(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 756 54" {...props}>
      <title>Formas de Pagamento</title>
      <g fill="none" fillRule="evenodd">
        <g fillRule="nonzero">
          <path
            fill="#253B80"
            d="M634.968 14.511h-10.651a1.48 1.48 0 0 0-1.463 1.25l-4.307 27.312a.888.888 0 0 0 .878 1.024h5.085c.729 0 1.35-.53 1.462-1.25l1.162-7.367a1.48 1.48 0 0 1 1.46-1.25h3.373c7.016 0 11.065-3.396 12.122-10.124.477-2.943.02-5.256-1.358-6.876-1.514-1.778-4.199-2.719-7.763-2.719Zm1.228 9.975c-.582 3.822-3.502 3.822-6.326 3.822h-1.607l1.128-7.137a.888.888 0 0 1 .876-.75h.737c1.923 0 3.738 0 4.675 1.097.56.654.73 1.626.517 2.968Zm30.609-.123h-5.1a.888.888 0 0 0-.877.75l-.226 1.426-.357-.517c-1.104-1.603-3.566-2.138-6.024-2.138-5.636 0-10.45 4.269-11.387 10.257-.488 2.987.206 5.843 1.9 7.835 1.554 1.832 3.778 2.595 6.424 2.595 4.541 0 7.06-2.92 7.06-2.92l-.228 1.417a.888.888 0 0 0 .876 1.028h4.594c.729 0 1.35-.53 1.462-1.25l2.757-17.458a.885.885 0 0 0-.874-1.025Zm-7.11 9.927c-.492 2.914-2.804 4.87-5.754 4.87-1.48 0-2.665-.475-3.425-1.375-.753-.894-1.04-2.166-.8-3.583.46-2.89 2.811-4.91 5.716-4.91 1.448 0 2.625.482 3.4 1.39.778.917 1.086 2.197.864 3.608Zm34.274-9.927h-5.126c-.49.001-.949.244-1.225.65l-7.07 10.413-2.996-10.007a1.484 1.484 0 0 0-1.42-1.056h-5.036a.888.888 0 0 0-.843 1.175l5.646 16.568-5.308 7.492a.888.888 0 0 0 .724 1.402h5.12c.484 0 .939-.237 1.216-.635l17.047-24.608a.888.888 0 0 0-.73-1.394Z"
          />
          <path
            fill="#179BD7"
            d="M710.938 14.511h-10.653a1.48 1.48 0 0 0-1.46 1.25l-4.308 27.312a.886.886 0 0 0 .875 1.024h5.467c.509 0 .942-.371 1.021-.875l1.223-7.742a1.48 1.48 0 0 1 1.46-1.25h3.37c7.018 0 11.066-3.396 12.125-10.124.478-2.943.019-5.256-1.36-6.876-1.512-1.778-4.195-2.719-7.76-2.719Zm1.229 9.975c-.581 3.822-3.501 3.822-6.326 3.822h-1.606l1.129-7.137a.885.885 0 0 1 .875-.75h.737c1.922 0 3.738 0 4.675 1.097.56.654.729 1.626.516 2.968Zm30.607-.123h-5.098a.883.883 0 0 0-.875.75l-.226 1.426-.358-.517c-1.104-1.603-3.565-2.138-6.022-2.138-5.636 0-10.449 4.269-11.386 10.257-.486 2.987.204 5.843 1.898 7.835 1.558 1.832 3.778 2.595 6.424 2.595 4.542 0 7.06-2.92 7.06-2.92l-.227 1.417a.888.888 0 0 0 .878 1.028h4.593a1.48 1.48 0 0 0 1.46-1.25l2.759-17.458a.89.89 0 0 0-.88-1.025Zm-7.11 9.927c-.489 2.914-2.805 4.87-5.754 4.87-1.478 0-2.665-.475-3.425-1.375-.754-.894-1.037-2.166-.8-3.583.462-2.89 2.81-4.91 5.715-4.91 1.449 0 2.626.482 3.402 1.39.78.917 1.088 2.197.862 3.608Zm13.123-19.03-4.372 27.813a.886.886 0 0 0 .875 1.024h4.395c.73 0 1.35-.529 1.463-1.25l4.31-27.311a.888.888 0 0 0-.875-1.026h-4.921a.89.89 0 0 0-.875.75Z"
          />
          <path
            fill="#253B80"
            d="m574.316 49.405.814-5.174-1.814-.042h-8.664l6.021-38.177a.492.492 0 0 1 .49-.417h14.607c4.85 0 8.197 1.009 9.944 3.001.82.934 1.341 1.91 1.593 2.986.265 1.127.27 2.474.011 4.117l-.019.12v1.053l.82.464a5.747 5.747 0 0 1 1.658 1.265c.701.799 1.154 1.814 1.346 3.018.198 1.238.132 2.712-.192 4.38-.374 1.918-.978 3.59-1.794 4.957a10.196 10.196 0 0 1-2.842 3.115c-1.084.77-2.372 1.353-3.828 1.727-1.411.368-3.02.553-4.784.553h-1.137c-.813 0-1.603.293-2.223.817a3.442 3.442 0 0 0-1.158 2.069l-.086.465-1.439 9.12-.065.334c-.017.106-.047.159-.09.195a.241.241 0 0 1-.15.054h-7.02Z"
          />
          <path
            fill="#179BD7"
            d="M598.894 15.94a25.55 25.55 0 0 1-.15.857c-1.926 9.892-8.517 13.309-16.934 13.309h-4.286c-1.03 0-1.897.747-2.057 1.763l-2.195 13.917-.621 3.945a1.096 1.096 0 0 0 1.082 1.267h7.602c.9 0 1.665-.654 1.806-1.541l.075-.387 1.431-9.083.092-.498c.14-.89.907-1.545 1.807-1.545h1.137c7.364 0 13.13-2.99 14.815-11.643.704-3.615.34-6.633-1.523-8.756a7.268 7.268 0 0 0-2.08-1.604Z"
          />
          <path
            fill="#222D65"
            d="M596.879 15.137a15.195 15.195 0 0 0-1.874-.416 23.802 23.802 0 0 0-3.778-.275h-11.45c-.9 0-1.666.655-1.805 1.545l-2.435 15.427-.07.45a2.08 2.08 0 0 1 2.057-1.762h4.286c8.417 0 15.008-3.419 16.935-13.309.057-.292.106-.577.15-.856a10.27 10.27 0 0 0-1.585-.668 14.086 14.086 0 0 0-.431-.136Z"
          />
          <path
            fill="#253B80"
            d="M577.972 15.99a1.82 1.82 0 0 1 1.805-1.543h11.45c1.357 0 2.623.089 3.778.276.782.123 1.554.307 2.307.55.568.188 1.096.41 1.584.668.573-3.655-.005-6.144-1.981-8.398C594.736 5.063 590.804 4 585.772 4h-14.608a2.088 2.088 0 0 0-2.064 1.765l-6.085 38.57a1.255 1.255 0 0 0 1.239 1.45h9.018l2.265-14.367 2.435-15.427Z"
          />
        </g>
        <path
          fill="#E9B040"
          fillRule="nonzero"
          d="M343.376 5c12.36 0 22.365 10.082 22.365 22.52 0 12.399-10.004 22.48-22.365 22.48a22.081 22.081 0 0 1-14.986-5.833l-.334.3C324.119 47.919 318.998 50 313.365 50 301.043 50 291 39.919 291 27.52 291 15.081 301.043 5 313.365 5c5.794 0 11.047 2.24 15.025 5.833A22.077 22.077 0 0 1 343.376 5Zm-2.896 16.764c-3.206 0-5.447 2.859-5.447 6.915 0 3.05 1.623 4.944 4.25 4.944.656 0 1.235-.117 2.124-.349l.425-2.587c-.773.386-1.468.579-2.047.579-1.43 0-2.28-1.042-2.28-2.78 0-2.512 1.275-4.288 3.09-4.288.696 0 1.314.192 2.164.618l.425-2.473c-.232-.076-.309-.115-.619-.231l-.966-.232a4.751 4.751 0 0 0-1.12-.116Zm-18.965.966h-2.395l-1.043 6.335-.116.734-.154.926c-.04.271-.078.58-.078.812 0 1.351.696 2.047 1.932 2.047.54 0 1.081-.078 1.738-.31l.309-2.046c-.155.078-.386.078-.657.078-.58 0-.811-.194-.811-.657 0-.116 0-.232.039-.387l.656-3.863h1.236l.31-2.279h-1.237l.27-1.39Zm4.905 1.159c-2.434 0-4.21 2.356-4.21 5.523 0 2.743 1.39 4.172 4.094 4.172.888 0 1.66-.116 2.51-.387l.387-2.317c-.85.425-1.622.58-2.51.58-1.39 0-2.125-.54-2.125-1.622 0-.078 0-.155.039-.271h4.789c.231-.965.309-1.622.309-2.318 0-2.047-1.275-3.36-3.283-3.36Zm-10.7.039c-2.703 0-3.669 1.429-3.63 3.128 0 1.16.54 1.97 1.815 2.588 1.004.464 1.159.618 1.159 1.004 0 .58-.425.85-1.39.85-.735 0-1.391-.116-2.164-.347l-.348 2.123.116.04.425.077c.154.038.348.077.657.077.54.077 1.004.077 1.313.077 2.55 0 3.747-.966 3.747-3.09 0-1.275-.502-2.009-1.7-2.588-1.042-.463-1.158-.579-1.158-1.004 0-.502.425-.734 1.197-.734.464 0 1.12.039 1.739.116l.347-2.124a13.988 13.988 0 0 0-2.124-.193Zm30.863 0c-.888 0-1.699.116-2.897.463l-.347 2.086.232-.039.309-.115c.54-.155 1.351-.232 2.047-.232 1.12 0 1.545.232 1.545.85 0 .154-.039.27-.116.579-.348-.039-.695-.077-.966-.077-2.704 0-4.25 1.313-4.25 3.592 0 1.506.89 2.51 2.203 2.51 1.12 0 1.931-.348 2.549-1.12l-.038.966h2.009v-.231l.038-.348.077-.464.077-.579.503-3.283c.165-.894.189-1.278.192-1.66v-.195c0-1.776-1.08-2.703-3.167-2.703Zm-38.24 0c-.89 0-1.7.116-2.898.463l-.348 2.086.232-.039.348-.115c.54-.155 1.313-.232 2.009-.232 1.12 0 1.545.232 1.545.85 0 .154 0 .27-.078.579-.385-.039-.733-.077-1.004-.077-2.704 0-4.249 1.313-4.249 3.592 0 1.506.889 2.51 2.202 2.51 1.12 0 1.931-.348 2.55-1.12l-.04.966h2.01l.038-.231.038-.348.039-.464.077-.579.541-3.283c.155-1.043.193-1.391.193-1.855 0-1.776-1.12-2.703-3.206-2.703Zm55.197-1.932h-2.395l-.54 3.245c-.658-.85-1.353-1.275-2.319-1.275-2.124 0-3.94 2.627-3.94 5.717 0 2.318 1.16 3.823 2.936 3.823.889 0 1.584-.309 2.241-1.004l-.116.889h2.28l1.853-11.395Zm-65.008 0h-3.476l-1.893 11.395h2.24l1.468-8.691.193 8.69h1.661l3.129-8.768-1.39 8.769h2.394l1.893-11.395h-3.786l-2.317 7.069-.116-7.069Zm36.85 2.047h-.233c-.81 0-1.274.387-2.008 1.506l.232-1.429h-2.202l-1.506 9.27h2.434c.888-5.678 1.12-6.643 2.278-6.643h.155c.232-1.12.54-1.932.927-2.666l-.078-.038Zm19.97 0h-.232c-.811 0-1.275.387-2.009 1.506l.232-1.429h-2.202l-1.468 9.27h2.395c.888-5.678 1.12-6.643 2.28-6.643h.154c.232-1.12.54-1.932.927-2.666l-.078-.038Zm-47.048 5.176c.193 0 .31.039.541.039-.038 1.39-.734 2.317-1.738 2.317-.54 0-.85-.347-.85-.927 0-.927.695-1.429 2.047-1.429Zm38.241 0c.154 0 .27.039.54.039-.038 1.39-.772 2.317-1.738 2.317-.54 0-.889-.347-.889-.927 0-.927.696-1.429 2.087-1.429Zm12.205-2.936c.85 0 1.275.618 1.275 1.816 0 1.815-.772 3.129-1.893 3.129-.85 0-1.274-.618-1.274-1.855 0-1.815.81-3.09 1.892-3.09Zm-32.446-.27c.734 0 1.159.425 1.159 1.197v.464h-2.704c.232-1.081.773-1.661 1.545-1.661Z"
        />
        <path
          fill="#1434CB"
          fillRule="nonzero"
          d="M230.39 8c-8.328 0-15.77 4.316-15.77 12.29 0 9.146 13.198 9.778 13.198 14.372 0 1.935-2.216 3.666-6.003 3.666-5.374 0-9.39-2.42-9.39-2.42l-1.718 8.048S215.333 46 221.476 46c9.104 0 16.269-4.528 16.269-12.64 0-9.663-13.254-10.276-13.254-14.54 0-1.516 1.82-3.176 5.596-3.176 4.26 0 7.736 1.76 7.736 1.76l1.681-7.773S235.723 8 230.39 8Zm-76.188.587L154 9.76s3.503.64 6.659 1.92c4.062 1.467 4.352 2.32 5.036 4.972l7.456 28.743h9.995l15.398-36.808h-9.972l-9.894 25.026-4.037-21.214c-.37-2.427-2.246-3.812-4.542-3.812h-15.897Zm48.352 0-7.823 36.808h9.51l7.795-36.808h-9.482Zm53.036 0c-2.293 0-3.508 1.227-4.4 3.372L237.26 45.395h9.972l1.93-5.573h12.148l1.173 5.573h8.799l-7.676-36.808h-8.015Zm1.297 9.944 2.955 13.812h-7.918l4.963-13.812Z"
        />
        <path
          fill="#0077A6"
          fillRule="nonzero"
          d="M440.135 34.979c0 1.352-.856 1.982-2.389 1.982h-2.93V35.61h2.93c.27 0 .496-.045.587-.135.09-.09.18-.226.18-.406 0-.18-.09-.36-.18-.45-.09-.09-.271-.135-.542-.135-1.396-.046-3.155.044-3.155-1.939 0-.902.586-1.893 2.164-1.893h3.02v1.352h-2.795c-.27 0-.45 0-.586.09-.135.135-.225.27-.225.496 0 .226.135.36.315.451.181.045.361.09.587.09h.81c.857 0 1.398.18 1.759.496.27.315.45.722.45 1.353Zm-6.355-1.353c-.361-.316-.902-.496-1.759-.496h-.81c-.226 0-.406-.045-.586-.09-.181-.09-.316-.225-.316-.45 0-.226.045-.362.225-.497.136-.09.316-.09.586-.09h2.795v-1.352h-3.02c-1.623 0-2.164.991-2.164 1.893 0 1.983 1.758 1.893 3.156 1.939.27 0 .45.044.54.135.09.09.18.27.18.45s-.09.316-.18.406c-.135.09-.315.135-.586.135h-2.93v1.352h2.93c1.533 0 2.39-.63 2.39-1.982 0-.631-.18-1.038-.451-1.353Zm-5.77 2.029h-3.56v-1.263h3.47v-1.261h-3.47v-1.173h3.56v-1.307h-5.048v6.31h5.048v-1.306Zm-6.671-4.688c-.496-.27-1.082-.316-1.848-.316h-3.47v6.31h1.532v-2.298h1.623c.54 0 .856.045 1.081.27.271.315.271.857.271 1.262v.766h1.487v-1.217c0-.586-.045-.856-.225-1.172a1.758 1.758 0 0 0-.766-.54c.405-.135 1.082-.676 1.082-1.668 0-.721-.271-1.127-.767-1.397Zm-8.564-.316h-4.823l-1.939 2.074-1.848-2.074h-6.085v6.31h5.995l1.939-2.073 1.848 2.073h2.93v-2.118h1.893c1.307 0 2.615-.36 2.615-2.118 0-1.713-1.353-2.074-2.525-2.074Zm7.347 2.615c-.225.09-.45.09-.721.09l-1.848.045v-1.443h1.848c.27 0 .54 0 .72.135.182.09.316.27.316.541s-.134.496-.315.632Zm-7.347.315h-1.984v-1.623h1.984c.541 0 .901.226.901.767 0 .54-.36.856-.901.856Zm-5.815.225 2.344-2.479v5.094l-2.344-2.615Zm-3.651 1.849h-3.741v-1.263h3.335v-1.261h-3.335v-1.173h3.786l1.668 1.849-1.713 1.848Zm32.634-9.557h-2.163l-2.84-4.732v4.732h-3.065l-.586-1.397h-3.155l-.586 1.397h-1.758c-.722 0-1.668-.18-2.209-.721-.496-.54-.766-1.262-.766-2.389 0-.946.135-1.803.81-2.479.451-.496 1.263-.721 2.3-.721h1.442v1.352h-1.442c-.54 0-.857.09-1.172.361-.27.27-.406.766-.406 1.442 0 .676.135 1.172.406 1.488.225.225.631.315 1.036.315h.677l2.163-4.958h2.254l2.524 5.95v-5.95h2.344l2.66 4.372v-4.372h1.532v6.31Zm-17.894-6.31h-1.533v6.31h1.533v-6.31Zm-3.2.27c-.497-.27-1.037-.27-1.804-.27h-3.47v6.31h1.487V23.8h1.622c.541 0 .902.046 1.127.271.27.316.225.857.225 1.217v.811h1.533v-1.262c0-.54-.045-.811-.27-1.127a1.886 1.886 0 0 0-.721-.54c.405-.18 1.081-.677 1.081-1.668 0-.721-.315-1.127-.81-1.442Zm-6.357 4.733h-3.515V23.53h3.47v-1.307h-3.47v-1.127h3.515v-1.307h-5.048v6.31h5.048v-1.307Zm-6.175-5.003h-2.479l-1.848 4.282-1.983-4.282h-2.434v5.95l-2.57-5.95h-2.253l-2.705 6.31h1.623l.586-1.397h3.155l.586 1.397h3.065V21.14l2.209 4.958h1.307l2.208-4.958v4.958h1.533v-6.31Zm24.431 3.56-1.037-2.478-1.036 2.479h2.073Zm-13.072-.99c-.225.135-.45.135-.766.135h-1.848v-1.397h1.848c.27 0 .585 0 .766.09.18.135.27.315.27.586 0 .27-.09.495-.27.586Zm-24.881.99 1.036-2.478 1.037 2.479h-2.073ZM439.775 0H386v22.718l1.848-4.192h3.921l.542 1.037v-1.037h4.597l1.037 2.254.992-2.254h14.65c.675 0 1.26.135 1.712.496v-.496h4.012v.496c.675-.361 1.532-.496 2.524-.496h5.814l.541 1.037v-1.037h4.282l.632 1.037v-1.037h4.191v8.835h-4.236l-.811-1.352v1.352h-5.275l-.585-1.443h-1.308l-.586 1.443h-2.75c-1.081 0-1.893-.27-2.433-.541v.54h-6.536v-2.028c0-.27-.045-.315-.226-.315h-.225v2.343h-12.621v-1.127l-.45 1.127h-2.66l-.45-1.081v1.081h-5.094l-.541-1.442H389.2l-.586 1.442H386V54h53.775V37.818c-.586.27-1.398.406-2.209.406h-3.922v-.542c-.45.361-1.262.542-2.028.542h-12.35v-2.029c0-.27-.045-.27-.271-.27h-.225v2.299h-4.057v-2.39c-.676.316-1.442.316-2.118.316h-.451v2.074h-4.959l-1.171-1.353-1.307 1.353h-7.98v-8.835h8.114l1.172 1.352 1.263-1.352h5.454c.63 0 1.668.09 2.118.54v-.54h4.868c.496 0 1.443.09 2.074.54v-.54h7.347v.541c.406-.36 1.172-.54 1.848-.54h4.102v.54c.45-.315 1.082-.54 1.893-.54h2.795V0Z"
        />
        <path
          fill="#053D4E"
          fillRule="nonzero"
          d="M38.77 41.693V22.322c0-.097.078-.176.175-.176h3.72c.096 0 .175.079.175.176v6.97h.053c.99-1.24 2.477-1.792 4.11-1.792 2.928 0 3.881 1.917 3.881 4.454v9.739a.18.18 0 0 1-.175.176h-3.734a.176.176 0 0 1-.176-.176v-9.385c0-1.096-1.15-1.433-1.88-1.433-.732 0-2.08.48-2.08 1.86v8.958a.176.176 0 0 1-.175.176h-3.72a.177.177 0 0 1-.175-.176ZM68.47 37.87v-6.68h-1.712a.176.176 0 0 1-.176-.175v-2.397a.187.187 0 0 1 .122-.169l2.268-.666 1.222-3.827a.18.18 0 0 1 .165-.119h2.025a.172.172 0 0 1 .175.176v3.77h3.01a.18.18 0 0 1 .176.175v3.06a.176.176 0 0 1-.176.172h-3.01v6.257c0 .649.359 1.043 1.269 1.043h1.741c.096.002.174.079.176.175v2.688a.182.182 0 0 1-.118.168c-1.054.394-1.649.63-2.867.63-3.057 0-4.29-1.773-4.29-4.281ZM126.725 37.87v-6.68h-1.712a.176.176 0 0 1-.176-.175v-2.397a.186.186 0 0 1 .122-.169l2.268-.666 1.222-3.827a.18.18 0 0 1 .165-.119h2.007a.173.173 0 0 1 .175.176v3.77h3.01a.18.18 0 0 1 .176.175v3.06a.176.176 0 0 1-.176.172h-3.013v6.257c0 .649.358 1.043 1.268 1.043h1.741c.097.002.174.079.176.175v2.688a.183.183 0 0 1-.118.168c-1.054.394-1.648.63-2.867.63-3.035 0-4.268-1.773-4.268-4.281ZM78.172 41.697V27.958c0-.097.078-.175.175-.175h3.354c.076.001.143.05.169.122 0 .06.386 1.397.386 1.397h.058c.985-1.236 2.45-1.792 4.085-1.792 2 0 2.963.903 3.5 2.283h.112c1.007-1.702 2.799-2.293 4.425-2.293 2.931 0 3.867 1.917 3.867 4.454v9.743a.176.176 0 0 1-.176.172h-3.74a.176.176 0 0 1-.176-.172V32.38c0-1.1-1.04-1.527-1.77-1.527-.853 0-2.111.538-2.168 1.792v9.052a.172.172 0 0 1-.176.172H86.36a.176.176 0 0 1-.173-.172V32.38c0-1.1-1.103-1.527-1.902-1.527-.735 0-2.036.567-2.036 1.95v8.894a.175.175 0 0 1-.171.172h-3.73a.176.176 0 0 1-.176-.172Z"
        />
        <path
          fill="#053D4E"
          d="M100.778 28.32v2.559a.18.18 0 0 0 .19.175l.16-.009c.43-.027 1.636-.102 2.77-.102 3.269 0 4.129.24 4.129 1.408v.914h-3.43c-2.003 0-4.68.735-4.68 4.397 0 2.422 1.409 4.479 4.06 4.479 1.572 0 2.875-.355 3.875-1.635l.343 1.234a.168.168 0 0 0 .169.129h3.558a.176.176 0 0 0 .176-.172v-9.435c0-3.888-2.13-4.762-5.734-4.762-1.832.042-3.656.26-5.447.648a.186.186 0 0 0-.14.172Zm4.998 7.464h2.236v1.118a2.18 2.18 0 0 1-2.272 2.272c-.942 0-1.92-.315-1.92-1.677 0-1.258.77-1.713 1.956-1.713Z"
        />
        <path
          fill="#053D4E"
          fillRule="nonzero"
          d="M114.46 41.697V27.958a.18.18 0 0 1 .175-.176h3.325c.085 0 .157.06.172.144l.398 2.15h.054c.896-1.846 2.755-2.509 4.35-2.562a.176.176 0 0 1 .183.175v3.82a.18.18 0 0 1-.176.176h-.953c-2.562 0-3.458 1.028-3.458 2.297v7.715a.175.175 0 0 1-.175.171h-3.72a.176.176 0 0 1-.176-.171Z"
        />
        <path
          fill="#053D4E"
          d="M59.316 27.5c-4.96 0-6.708 2.845-6.708 7.328 0 4.497 1.72 7.324 6.708 7.324s6.737-2.842 6.737-7.324c0-4.483-1.777-7.328-6.737-7.328Zm0 11.237c-1.945 0-2.304-1.487-2.304-3.91 0-2.425.316-3.887 2.29-3.887 1.975 0 2.333 1.465 2.333 3.888 0 2.422-.373 3.909-2.319 3.909Z"
        />
        <path
          fill="#F04E23"
          d="M26.783 18.197c3.214 5.128 4.529 11.399 4.332 15.233l.001.07.002.07c-.071 8.593-7.095 15.5-15.688 15.43C6.837 48.927-.07 41.903 0 33.311a10.864 10.864 0 0 1 .036-.803c.01-.115.022-.234.022-.351a34.408 34.408 0 0 1 .09-.889c.587-5.658 3.69-11.75 7.112-15.333a.1.1 0 1 1 .165.115c-.419.9-1.29 3.483-.079 3.565.405 0 .61-.236.85-.716.287-.583.572-1.371.898-2.276C10.37 13.082 12.29 7.757 17.59 6.01c.104-.035.23.022.136.172a7.65 7.65 0 0 0-.104 8.06c1.083 1.79 2.211 1.923 2.828 1.39.763-.67.566-2.638.236-3.268-.054-.104.061-.19.158-.126 2.826 1.91 3.223 4.312 3.504 6.009.2 1.207.34 2.057 1.254 2.118 1.244.086 1.194-1.326.979-2.11-.032-.14.114-.19.2-.058ZM8.343 33.384a7.217 7.217 0 1 0 14.433.114 7.217 7.217 0 0 0-14.434-.114Z"
        />
        <g fillRule="nonzero">
          <path
            fill="#010101"
            d="M460 4h.933v24.767H460V4Zm1.866 0h.932v24.767h-.932V4Zm5.6 0h.933v24.767h-.933V4Zm18.157 0h.932v24.767h-.932V4Zm6.07 0h.932v24.767h-.933V4Zm5.885 0h.933v24.767h-.933V4Zm1.517 0h.933v24.767h-.933V4Zm1.52 0h.932v24.767h-.933V4Zm1.706 0h.933v24.767h-.933V4Zm1.707 0h.933v24.767h-.933V4Zm4.746 0h.932v24.767h-.932V4Zm11.765 0h.933v24.767h-.933V4Zm12.273 0h.933v24.767h-.933V4Zm3.357 0h.932v24.767h-.932V4Zm2.426 0h.932v24.767h-.932V4Zm1.493 0h.933v24.767h-.933V4Zm-66.463 0h.933v24.767h-.933V4Zm-9.333 0h1.679v24.767h-1.68V4Zm11.084 0h1.68v24.767h-1.68V4Zm7.783 0h1.68v24.767h-1.68V4Zm3.984 0h1.68v24.767h-1.68V4Zm6.263 0h1.679v24.767h-1.68V4Zm17.853 0h2.612v24.767h-2.612V4Zm3.412 0h2.054v24.767h-2.054V4Zm3.6 0h1.68v24.767h-1.68V4Zm4.761 0h2.8v24.767h-2.8V4Zm5.484 0h2.8v24.767h-2.8V4Zm-59.184 0h3.358v24.767h-3.358V4Zm8.706 0h2.612v24.767h-2.612V4Z"
          />
          <path
            fill="#000"
            d="M461.208 50.757V36.582h5.32a6.583 6.583 0 0 1 2.606.43 3.35 3.35 0 0 1 1.536 1.324c.36.559.553 1.208.557 1.872 0 .605-.17 1.198-.493 1.71a3.521 3.521 0 0 1-1.49 1.296c.783.201 1.479.652 1.981 1.285.465.618.708 1.374.691 2.147a4.141 4.141 0 0 1-.42 1.852c-.238.517-.596.97-1.043 1.32a4.34 4.34 0 0 1-1.553.702 9.439 9.439 0 0 1-2.285.237h-5.407Zm1.877-8.218h3.066a6.84 6.84 0 0 0 1.783-.165 1.98 1.98 0 0 0 1.077-.705c.252-.362.379-.796.362-1.237a2.28 2.28 0 0 0-.338-1.242 1.713 1.713 0 0 0-.959-.737 8.076 8.076 0 0 0-2.157-.198h-2.834v4.284Zm0 6.547h3.53a8.12 8.12 0 0 0 1.277-.067c.383-.062.75-.197 1.083-.396.304-.197.55-.47.715-.793.192-.366.289-.774.281-1.188a2.355 2.355 0 0 0-.406-1.386 2.143 2.143 0 0 0-1.126-.822 6.985 6.985 0 0 0-2.074-.238h-3.28v4.89Zm11.572-5.232a7.704 7.704 0 0 1 1.895-5.528 6.444 6.444 0 0 1 4.894-1.996 6.789 6.789 0 0 1 3.539.939 6.125 6.125 0 0 1 2.402 2.616 8.496 8.496 0 0 1 .826 3.804 8.353 8.353 0 0 1-.87 3.858 5.941 5.941 0 0 1-2.465 2.575 7.044 7.044 0 0 1-3.442.875 6.704 6.704 0 0 1-3.577-.964 6.222 6.222 0 0 1-2.39-2.642 8 8 0 0 1-.812-3.537Zm1.935.03a5.68 5.68 0 0 0 1.386 4.038 4.533 4.533 0 0 0 3.458 1.475 4.525 4.525 0 0 0 3.474-1.491 6.02 6.02 0 0 0 1.368-4.226 7.262 7.262 0 0 0-.576-3.023 4.496 4.496 0 0 0-1.711-2.002 4.64 4.64 0 0 0-2.53-.71 4.797 4.797 0 0 0-3.427 1.368 6.112 6.112 0 0 0-1.442 4.569v.001Zm14.696 6.873V36.582h1.876v12.502h6.98v1.673h-8.856Zm11.863 0V36.582h10.25v1.673h-8.374v4.341h7.842v1.664h-7.842v4.824h8.703v1.673h-10.579Zm17.51 0V38.255h-4.67v-1.673h11.236v1.673h-4.69v12.502h-1.875Zm8.295-6.903a7.704 7.704 0 0 1 1.9-5.528 6.444 6.444 0 0 1 4.893-1.996 6.789 6.789 0 0 1 3.539.939 6.125 6.125 0 0 1 2.402 2.616 8.496 8.496 0 0 1 .826 3.804 8.353 8.353 0 0 1-.87 3.858 5.941 5.941 0 0 1-2.473 2.577 7.044 7.044 0 0 1-3.442.875c-1.26.02-2.5-.314-3.579-.966a6.222 6.222 0 0 1-2.384-2.642 8 8 0 0 1-.812-3.537Zm1.935.03a5.68 5.68 0 0 0 1.386 4.038 4.533 4.533 0 0 0 3.458 1.475 4.525 4.525 0 0 0 3.485-1.49 6.02 6.02 0 0 0 1.369-4.226 7.262 7.262 0 0 0-.594-3.022 4.496 4.496 0 0 0-1.711-2.002 4.64 4.64 0 0 0-2.53-.71 4.797 4.797 0 0 0-3.427 1.368 6.112 6.112 0 0 0-1.438 4.567l.002.001Z"
          />
        </g>
      </g>
    </svg>
  );
}

BlackPage.getInitialProps = async ({ query }) => {
  if (
    getQueryStringValue(query, "isSpecialOfferWithCaixaPreta") === "true" ||
    getQueryStringValue(query, "offer") === "caixaPreta"
  ) {
    return {
      version: "SPECIAL_OFFER",
    };
  }
  if (
    getQueryStringValue(query, "isSpecialPremiumOffer") === "true" ||
    getQueryStringValue(query, "offer") === "premium"
  ) {
    return {
      version: "PREMIUM",
    };
  }
  return {
    version: null,
  };
};

export default BlackPage;
